import type { SfxId } from './ids.js';
import { arpeggio, cardSlap, diceRattle, noiseBurst, pluck, softDeny, swish, woodTock } from './synth/voices.js';

interface PlayArgs {
  readonly detune: number;
  readonly random: () => number;
}

/**
 * One entry per `SfxId` — the closed union means a sound without a voice fails typecheck.
 * Each entry picks the synth voices and their parameters; two or three "variants" come free from
 * the per-play detune and the occasional extra burst the random source decides on.
 */
type Voice = (ctx: BaseAudioContext, dest: AudioNode, t0: number, args: PlayArgs & { readonly pan: number }) => void;

const WIN_RISE = [261.6, 329.6, 392.0, 523.3];
const LOSE_FALL = [311.1, 233.1];
const LEVEL_RISE = [392.0, 523.3, 659.3];

/** Stereo panning is done with a stereo panner node when the caller passes a pan. */
function withPan(ctx: BaseAudioContext, dest: AudioNode, pan: number): AudioNode {
  if (pan === 0 || typeof ctx.createStereoPanner !== 'function') return dest;
  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(dest);
  return panner;
}

export function playSfx(id: SfxId, ctx: BaseAudioContext, dest: AudioNode, t0: number, args: PlayArgs & { readonly pan: number }): void {
  const out = withPan(ctx, dest, args.pan);
  const a = { detune: args.detune, random: args.random };
  switch (id) {
    // — All games
    case 'ui-tap':
      noiseBurst(ctx, out, t0, { dur: 0.015, freq: 3600, q: 2, gain: 0.1, detune: a.detune });
      break;
    case 'select':
      pluck(ctx, out, t0, 660, { gain: 0.14, dur: 0.14, detune: a.detune });
      break;
    case 'deselect':
      pluck(ctx, out, t0, 440, { gain: 0.12, dur: 0.12, detune: a.detune });
      break;
    case 'illegal':
      softDeny(ctx, out, t0, a);
      break;
    case 'win':
      arpeggio(ctx, out, t0, WIN_RISE, { gain: 0.22, detune: a.detune });
      break;
    case 'lose':
      arpeggio(ctx, out, t0, LOSE_FALL, { gain: 0.18, step: 0.2, detune: a.detune });
      break;
    case 'draw':
      arpeggio(ctx, out, t0, [329.6, 329.6], { gain: 0.14, step: 0.18, detune: a.detune });
      break;
    case 'new-game':
      arpeggio(ctx, out, t0, [261.6, 392.0], { gain: 0.16, detune: a.detune });
      break;
    case 'hint':
      pluck(ctx, out, t0, 880, { gain: 0.12, dur: 0.3, detune: a.detune });
      break;
    case 'level-up':
      arpeggio(ctx, out, t0, LEVEL_RISE, { gain: 0.2, detune: a.detune });
      break;
    // — Chess
    case 'chess-slide':
      woodTock(ctx, out, t0, { freq: 210, detune: a.detune });
      break;
    case 'chess-capture':
      woodTock(ctx, out, t0, { freq: 150, dur: 0.12, gain: 0.6, detune: a.detune });
      noiseBurst(ctx, out, t0 + 0.02, { dur: 0.05, freq: 900, q: 1, gain: 0.2 });
      break;
    case 'chess-check':
      pluck(ctx, out, t0, 523.3, { gain: 0.18, dur: 0.2, detune: a.detune });
      pluck(ctx, out, t0 + 0.09, 659.3, { gain: 0.18, dur: 0.25, detune: a.detune });
      break;
    case 'chess-castle':
      woodTock(ctx, out, t0, { freq: 210, detune: a.detune });
      woodTock(ctx, out, t0 + 0.11, { freq: 175, detune: a.detune });
      break;
    case 'chess-promote':
      arpeggio(ctx, out, t0, [392.0, 523.3, 784.0], { gain: 0.18, detune: a.detune });
      break;
    // — Checkers
    case 'checkers-slide':
      woodTock(ctx, out, t0, { freq: 185, detune: a.detune });
      break;
    case 'checkers-jump':
      woodTock(ctx, out, t0, { freq: 165, dur: 0.1, gain: 0.55, detune: a.detune });
      woodTock(ctx, out, t0 + 0.1, { freq: 130, detune: a.detune });
      break;
    case 'checkers-king':
      arpeggio(ctx, out, t0, [329.6, 415.3, 523.3, 659.3], { gain: 0.2, detune: a.detune });
      break;
    // — Backgammon
    case 'dice-roll':
      diceRattle(ctx, out, t0, a);
      break;
    case 'checker-clack':
      woodTock(ctx, out, t0, { freq: 240, dur: 0.06, gain: 0.45, detune: a.detune });
      break;
    case 'bg-hit':
      woodTock(ctx, out, t0, { freq: 120, dur: 0.14, gain: 0.6, detune: a.detune });
      noiseBurst(ctx, out, t0 + 0.01, { dur: 0.08, freq: 700, q: 0.8, gain: 0.25 });
      break;
    case 'bg-enter':
      woodTock(ctx, out, t0, { freq: 200, detune: a.detune });
      break;
    case 'bg-bear-off':
      swish(ctx, out, t0, { dur: 0.22, gain: 0.2, detune: a.detune });
      woodTock(ctx, out, t0 + 0.16, { freq: 260, dur: 0.06, gain: 0.35, detune: a.detune });
      break;
    case 'cube-turn':
      woodTock(ctx, out, t0, { freq: 300, dur: 0.05, gain: 0.4, detune: a.detune });
      pluck(ctx, out, t0 + 0.06, 740, { gain: 0.14, dur: 0.25, detune: a.detune });
      break;
    // — Cards
    case 'card-deal': {
      // A quick burst of slaps; how many depends on the random source, so hands differ.
      const count = 3 + Math.floor(a.random() * 3);
      for (let i = 0; i < count; i++) {
        cardSlap(ctx, out, t0 + i * 0.055, { gain: 0.3 - i * 0.04, detune: a.detune + (a.random() - 0.5) * 0.4 });
      }
      break;
    }
    case 'card-play':
      cardSlap(ctx, out, t0, { detune: a.detune });
      break;
    case 'trick-sweep':
      swish(ctx, out, t0, { dur: 0.32, gain: 0.22, detune: a.detune });
      break;
    case 'trump-chosen':
      pluck(ctx, out, t0, 587.3, { gain: 0.18, dur: 0.4, detune: a.detune });
      break;
    case 'contract-made':
      arpeggio(ctx, out, t0, WIN_RISE, { gain: 0.2, detune: a.detune });
      break;
    case 'contract-failed':
      arpeggio(ctx, out, t0, LOSE_FALL, { gain: 0.16, step: 0.22, detune: a.detune });
      break;
    case 'guess-lock':
      cardSlap(ctx, out, t0, { gain: 0.32, detune: a.detune });
      pluck(ctx, out, t0 + 0.05, 523.3, { gain: 0.14, dur: 0.3, detune: a.detune });
      break;
    case 'round-won':
      arpeggio(ctx, out, t0, [329.6, 392.0, 523.3], { gain: 0.15, step: 0.09, detune: a.detune });
      break;
    case 'round-lost':
      arpeggio(ctx, out, t0, [293.7, 246.9], { gain: 0.12, step: 0.14, detune: a.detune });
      break;
    case 'round-draw':
      arpeggio(ctx, out, t0, [329.6, 329.6], { gain: 0.1, step: 0.12, detune: a.detune });
      break;
    case 'turn-yours':
      pluck(ctx, out, t0, 784.0, { gain: 0.07, dur: 0.16, detune: a.detune });
      break;
  }
}
